import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  chartColors, 
  pastelColors, 
  dataTypeColors, 
  getMakerColor,
  getColorWithOpacity
} from '../../utils/colorUtils';

// Register all necessary ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const EnhancedChart = forwardRef(({
  type = 'bar',
  data,
  options = {},
  height = '100%',
  enhancedColors = true,
  colorPalette = 'default',
  horizontalBar = false
}, ref) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const isHorizontalBar = type === 'bar' && horizontalBar;
  
  // Expose Chart.js instance to parents (pin/download/fullscreen need a live canvas reference).
  // Do not snapshot chartRef.current at mount — it is null until the chart mounts.
  useImperativeHandle(ref, () => ({
    get canvas() {
      return chartRef.current?.canvas;
    },
    get chartInstance() {
      return chartRef.current;
    },
  }), []);

  // Get the appropriate color palette
  const getColorPalette = () => {
    switch (colorPalette) {
      case 'pastel':
        return pastelColors;
      case 'ev':
        return [dataTypeColors.ev.primary, ...chartColors.slice(1)];
      case 'ice':
        return [dataTypeColors.ice.primary, ...chartColors.slice(1)];
      case 'combined':
        return [dataTypeColors.ev.primary, dataTypeColors.ice.primary, ...chartColors.slice(2)];
      default:
        return chartColors;
    }
  };

  // Enhance the data with better colors if enabled
  const enhancedData = enhancedColors
    ? {
        ...data,
        datasets: data.datasets.map((dataset, index) => {
          // Handle special cases for datasets with specific labels
          let backgroundColor, borderColor;

          if (dataset.label?.toLowerCase().includes('ev')) {
            backgroundColor = getColorWithOpacity(dataTypeColors.ev.primary, 0.6);
            borderColor = dataTypeColors.ev.primary;
          } else if (dataset.label?.toLowerCase().includes('ice')) {
            backgroundColor = getColorWithOpacity(dataTypeColors.ice.primary, 0.6);
            borderColor = dataTypeColors.ice.primary;
          } else if (dataset.label?.toLowerCase().includes('total')) {
            backgroundColor = getColorWithOpacity(dataTypeColors.combined.primary, 0.6);
            borderColor = dataTypeColors.combined.primary;
          } else {
            // For datasets with maker names, use maker-specific colors
            const colorPalette = getColorPalette();
            const makerName = dataset.label;
            backgroundColor = getColorWithOpacity(
              getMakerColor(makerName, index),
              type === 'line' ? 0.2 : 0.6
            );
            borderColor = getMakerColor(makerName, index);
          }

          return {
            ...dataset,
            backgroundColor: dataset.backgroundColor || backgroundColor,
            borderColor: dataset.borderColor || borderColor,
            borderWidth: dataset.borderWidth || 2,
            pointBackgroundColor: dataset.pointBackgroundColor || borderColor,
            pointBorderColor: dataset.pointBorderColor || theme.palette.background.paper,
            pointHoverBackgroundColor: dataset.pointHoverBackgroundColor || theme.palette.background.paper,
            pointHoverBorderColor: dataset.pointHoverBorderColor || borderColor,
            pointRadius: dataset.pointRadius || 4,
            pointHoverRadius: dataset.pointHoverRadius || 6,
            ...(type === 'line' && { tension: dataset.tension || 0.2 }),
            ...(type === 'line' && dataset.fill && { 
              fill: true,
              backgroundColor: alpha(borderColor, 0.1)
            }),
          };
        }),
      }
    : data;

  // Set common chart options with good defaults
  const enhancedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animations: {
      tension: {
        duration: 1000,
        easing: 'linear'
      }
    },
    plugins: {
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        bodyFont: {
          family: theme.typography.fontFamily,
          size: 13
        },
        titleFont: {
          family: theme.typography.fontFamily,
          size: 14,
          weight: 'bold'
        },
        boxPadding: 4,
        usePointStyle: true,
        callbacks: {
          labelTextColor: () => theme.palette.text.primary,
          title: function(context) {
            if (!context.length) return '';
            const item = context[0];
            const ds = item.dataset;
            const idx = item.dataIndex;
            if (ds.fullLabels && ds.fullLabels[idx] != null) {
              return String(ds.fullLabels[idx]);
            }
            const lbl = item.chart?.data?.labels?.[idx];
            return lbl != null ? String(lbl) : '';
          },
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const v = isHorizontalBar && type === 'bar'
              ? context.parsed.x
              : context.parsed.y;
            if (v !== null && v !== undefined) {
              label += Number(v).toLocaleString();
            }
            return label;
          }
        }
      },
      legend: {
        display: !isHorizontalBar,
        position: 'top',
        align: 'center',
        labels: {
          padding: 15,
          boxWidth: 12,
          boxHeight: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            family: theme.typography.fontFamily,
            size: 12,
          },
          color: theme.palette.text.secondary
        }
      }
    },
    indexAxis: isHorizontalBar ? 'y' : 'x',
    scales: type !== 'pie' && type !== 'doughnut' ? (
      isHorizontalBar ? {
        x: {
          beginAtZero: true,
          grid: {
            display: true,
            color: alpha(theme.palette.divider, 0.1),
            borderColor: theme.palette.divider,
            tickColor: theme.palette.divider
          },
          ticks: {
            padding: 8,
            font: {
              family: theme.typography.fontFamily,
              size: 11
            },
            color: theme.palette.text.secondary,
            callback: function(value) {
              return value.toLocaleString();
            }
          }
        },
        y: {
          reverse: false,
          grid: {
            display: false
          },
          border: {
            display: false
          },
          ticks: {
            autoSkip: false,
            padding: 4,
            font: {
              family: theme.typography.fontFamily,
              size: 9,
              lineHeight: 1.2
            },
            color: theme.palette.text.secondary
          }
        }
      } : {
        x: {
          grid: {
            display: true,
            color: alpha(theme.palette.divider, 0.1),
            borderColor: theme.palette.divider,
            tickColor: theme.palette.divider
          },
          ticks: {
            padding: 8,
            font: {
              family: theme.typography.fontFamily,
              size: 11
            },
            color: theme.palette.text.secondary
          }
        },
        y: {
          grid: {
            display: true,
            color: alpha(theme.palette.divider, 0.1),
            borderColor: theme.palette.divider,
            tickColor: theme.palette.divider
          },
          ticks: {
            padding: 8,
            font: {
              family: theme.typography.fontFamily,
              size: 11
            },
            color: theme.palette.text.secondary,
            callback: function(value) {
              return value.toLocaleString();
            }
          },
          beginAtZero: true
        }
      }
    ) : undefined,
  };

  const { plugins: optsPlugins, scales: optsScales, ...restOptions } = options || {};
  const mergedOptions = {
    ...enhancedOptions,
    ...restOptions,
    plugins: {
      ...enhancedOptions.plugins,
      ...optsPlugins,
      tooltip: {
        ...enhancedOptions.plugins.tooltip,
        ...optsPlugins?.tooltip,
        callbacks: {
          ...enhancedOptions.plugins.tooltip.callbacks,
          ...optsPlugins?.tooltip?.callbacks,
        },
      },
      legend: {
        ...enhancedOptions.plugins.legend,
        ...optsPlugins?.legend,
      },
    },
    scales: optsScales && enhancedOptions.scales
      ? { ...enhancedOptions.scales, ...optsScales }
      : enhancedOptions.scales,
  };

  // Add gradient background for line charts with fill
  useEffect(() => {
    if (chartRef.current && type === 'line') {
      const chart = chartRef.current;
      
      if (chart) {
        chart.data.datasets.forEach((dataset, i) => {
          if (dataset.fill) {
            const ctx = chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, chart.height);
            const color = dataset.borderColor;
            
            gradient.addColorStop(0, alpha(color, 0.3));
            gradient.addColorStop(1, alpha(color, 0.0));
            
            dataset.backgroundColor = gradient;
          }
        });
      }
    }
  }, [data, type]);

  // Render the appropriate chart based on type
  const renderChart = () => {
    switch (type) {
      case 'bar':
        return <Bar ref={chartRef} data={enhancedData} options={mergedOptions} />;
      case 'line':
        return <Line ref={chartRef} data={enhancedData} options={mergedOptions} />;
      case 'pie':
        return <Pie ref={chartRef} data={enhancedData} options={mergedOptions} />;
      case 'doughnut':
        return <Doughnut ref={chartRef} data={enhancedData} options={mergedOptions} />;
      default:
        return <Bar ref={chartRef} data={enhancedData} options={mergedOptions} />;
    }
  };

  return <Box sx={{ height, width: '100%' }}>{renderChart()}</Box>;
});

EnhancedChart.displayName = 'EnhancedChart';

export default EnhancedChart; 